import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc,
  serverTimestamp, query, orderBy,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { useLocation } from "react-router-dom";
import { FiEdit2, FiTrash2, FiPlus, FiX, FiImage } from "react-icons/fi";

const emptyForm = {
  title: "", description: "", eventDate: "", time: "", venue: "",
  organizer: "", registrationLink: "", image: "",
};

const Events = () => {
  const location = useLocation();
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const snap = await getDocs(query(collection(db, "events"), orderBy("eventDate", "asc")));
      setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (location.state?.openForm) {
      setForm(emptyForm);
      setEditId(null);
      setShowForm(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
      });
      setForm((f) => ({ ...f, image: base64 }));
    } catch (e) { alert("Upload failed."); }
    setUploading(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        await updateDoc(doc(db, "events", editId), { ...form, updatedAt: serverTimestamp() });
      } else {
        await addDoc(collection(db, "events"), { ...form, createdAt: serverTimestamp() });
      }
      setShowForm(false); setForm(emptyForm); setEditId(null);
      fetchData();
    } catch (e) { alert("Save failed: " + e.message); }
    setSaving(false);
  };

  const handleEdit = (event) => {
    setForm({
      title: event.title || "", description: event.description || "",
      eventDate: event.eventDate || "", time: event.time || "",
      venue: event.venue || "", organizer: event.organizer || "",
      registrationLink: event.registrationLink || "", image: event.image || "",
    });
    setEditId(event.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await deleteDoc(doc(db, "events", id));
    fetchData();
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-extrabold text-black">Events</h1>
            <p className="text-sm text-gray-400">Manage upcoming events</p>
          </div>
          <button onClick={() => { setForm(emptyForm); setEditId(null); setShowForm(true); }}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#e84fa5] text-white font-bold uppercase text-[13px] rounded-lg hover:bg-pink-600 transition">
            <FiPlus /> Add Event
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#e84fa5] border-t-transparent rounded-full animate-spin"></div></div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-5 py-3 text-[12px] font-bold text-gray-500 uppercase">Event</th>
                  <th className="text-left px-5 py-3 text-[12px] font-bold text-gray-500 uppercase">Date</th>
                  <th className="text-left px-5 py-3 text-[12px] font-bold text-gray-500 uppercase">Venue</th>
                  <th className="text-right px-5 py-3 text-[12px] font-bold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-10 text-gray-400 text-sm">No events yet.</td></tr>
                )}
                {events.map((ev) => (
                  <tr key={ev.id} className="border-b hover:bg-gray-50 transition">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        {ev.image && <img src={ev.image} alt="" className="w-10 h-10 object-cover rounded-lg" />}
                        <span className="font-semibold text-[13px] text-black">{ev.title}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-[13px] text-gray-500">
                      {ev.eventDate ? new Date(ev.eventDate).toLocaleDateString() : "—"}
                      {ev.time && <span className="block text-[11px] text-gray-400">{ev.time}</span>}
                    </td>
                    <td className="px-5 py-4 text-[13px] text-gray-500">{ev.venue || "—"}</td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => handleEdit(ev)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"><FiEdit2 size={15} /></button>
                        <button onClick={() => handleDelete(ev.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><FiTrash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8 px-4">
            <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl">
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-lg font-extrabold">{editId ? "Edit Event" : "Add Event"}</h2>
                <button onClick={() => { setShowForm(false); setEditId(null); setForm(emptyForm); }} className="text-gray-400 hover:text-gray-700"><FiX size={20} /></button>
              </div>
              <form onSubmit={handleSave} className="p-6 space-y-4">
                {/* Event Image */}
                <div>
                  <label className="block text-[12px] font-bold text-gray-600 mb-2">Event Image</label>
                  {form.image && (
                    <div className="relative inline-block mb-2">
                      <img src={form.image} alt="" className="h-24 w-auto object-cover rounded-lg border" />
                      <button type="button" onClick={() => setForm((f) => ({ ...f, image: "" }))} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"><FiX /></button>
                    </div>
                  )}
                  <label className="flex items-center gap-2 cursor-pointer w-fit px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#e84fa5] text-[13px] text-gray-500 hover:text-[#e84fa5] transition">
                    <FiImage /> {uploading ? "Uploading..." : "Upload Image"}
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                  </label>
                </div>

                <div>
                  <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Event Title *</label>
                  <input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="Event title" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Event Date *</label>
                    <input required type="date" value={form.eventDate} onChange={(e) => setForm((f) => ({ ...f, eventDate: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
                  </div>
                  <div>
                    <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Time</label>
                    <input value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="e.g. 2:00 PM - 5:00 PM" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Venue / Location</label>
                    <input value={form.venue} onChange={(e) => setForm((f) => ({ ...f, venue: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="City, State or online" />
                  </div>
                  <div>
                    <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Organizer</label>
                    <input value={form.organizer} onChange={(e) => setForm((f) => ({ ...f, organizer: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="Organizer name" />
                  </div>
                </div>

                <div>
                  <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Description</label>
                  <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={3}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5] resize-none" placeholder="Event description..." />
                </div>

                <div>
                  <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Registration Link</label>
                  <input type="url" value={form.registrationLink} onChange={(e) => setForm((f) => ({ ...f, registrationLink: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="https://..." />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={() => { setShowForm(false); setEditId(null); setForm(emptyForm); }}
                    className="px-5 py-2.5 border border-gray-200 rounded-lg text-[13px] font-semibold hover:bg-gray-50 transition">Cancel</button>
                  <button type="submit" disabled={saving || uploading}
                    className="px-6 py-2.5 bg-[#e84fa5] text-white font-bold text-[13px] rounded-lg hover:bg-pink-600 transition disabled:opacity-60">
                    {saving ? "Saving..." : editId ? "Update Event" : "Add Event"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Events;
